/**
 * Vanilla JS implementation for HIDDEN anchor type
 *
 * This minimal module handles:
 * - Button click detection (.compatio-builder-button)
 * - Hash-based deep linking (#configure&model=xyz)
 * - Body scroll locking when builder opens
 * - Loading spinner display
 * - Async React + BuilderLoader loading
 *
 * No dependencies - pure vanilla JS for minimal bundle size
 */

// Minimal inline CSS for spinner - only loads for HIDDEN anchors
// Includes critical Tailwind base resets needed for utility classes to work
const SPINNER_CSS = `
/* Critical Tailwind base resets for spinner */
.sb3-hidden-root *, .sb3-hidden-root *::before, .sb3-hidden-root *::after {
  box-sizing: border-box;
  border: 0 solid #e5e7eb;
}
.sb3-contain {
  width: 100%;
  max-width: 100%;
  margin: 0;
  line-height: 1.5;
}
/* Utility classes for spinner */
.sb3-hidden-root .sb3-fixed { position: fixed; }
.sb3-hidden-root .sb3-top-0 { top: 0; }
.sb3-hidden-root .sb3-left-0 { left: 0; }
.sb3-hidden-root .sb3-w-screen { width: 100vw; }
.sb3-hidden-root .sb3-max-w-screen { max-width: 100vw; }
.sb3-hidden-root .sb3-h-screen { height: 100vh; }
.sb3-hidden-root .sb3-bg-white { background-color: #fff; }
.sb3-hidden-root .sb3-overflow-auto { overflow: auto; }
.sb3-hidden-root .sb3-z-\\[20000\\] { z-index: 20000; }
.sb3-hidden-root .sb3-bg-\\[rgba\\(0\\,0\\,0\\,0\\.75\\)\\] { background-color: rgba(0,0,0,0.75); }
.sb3-hidden-root .sb3-flex { display: flex; }
.sb3-hidden-root .sb3-items-center { align-items: center; }
.sb3-hidden-root .sb3-justify-center { justify-content: center; }
.sb3-hidden-root .sb3-w-10 { width: 2.5rem; }
.sb3-hidden-root .sb3-h-10 { height: 2.5rem; }
.sb3-hidden-root .sb3-border-4 { border-width: 4px; border-style: solid; }
.sb3-hidden-root .sb3-border-blue-500 { border-color: #3b82f6; }
.sb3-hidden-root .sb3-border-t-transparent { border-top-color: transparent; }
.sb3-hidden-root .sb3-rounded-full { border-radius: 9999px; }
.sb3-hidden-root .sb3-animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

let stylesInjected = false;

function injectSpinnerStyles() {
  if (stylesInjected) return;

  const style = document.createElement('style');
  style.textContent = SPINNER_CSS;
  document.head.appendChild(style);
  stylesInjected = true;
}

export class HiddenAnchor {
  constructor(configPromise) {
    this.state = {
      isOpen: false,
      modelId: null,
      baseProductSku: null,
      originalScroll: null,
      builderRootElement: null,
      builderContainer: null,
      reactRoot: null
    };

    // Store config promise to wait for it before opening builder
    // If no promise provided, create a resolved one (for backwards compatibility)
    this.configPromise = configPromise || Promise.resolve();

    // Bound methods for event listeners (needed for cleanup)
    this.clickHandler = this.handleButtonClick.bind(this);
    this.hashHandler = this.handleHashChange.bind(this);

    this.init();
  }

  /**
   * Initialize event listeners
   */
  init() {
    // Inject minimal CSS for spinner (only when HIDDEN anchor present)
    injectSpinnerStyles();

    // Listen for clicks on .compatio-builder-button elements
    // Use capture phase to catch events before other handlers
    document.addEventListener('click', this.clickHandler, true);

    // Listen for hash changes (deep linking)
    window.addEventListener('hashchange', this.hashHandler);

    // Check initial hash on page load
    this.handleHashChange();
  }

  /**
   * Handle clicks on .compatio-builder-button elements
   */
  handleButtonClick(event) {
    const target = event.target;
    const builderButton = target.closest('.compatio-builder-button');

    if (builderButton) {
      // Stop the event immediately
      event.preventDefault();

      // Extract data attributes
      const modelId = builderButton.getAttribute('modelid');
      const baseProductSku = builderButton.getAttribute('product-sku');
      const sessionId = builderButton.getAttribute('session-id') || undefined;
      const resume = builderButton.getAttribute('resume') === 'true';
      const inputParameter = builderButton.getAttribute('input-parameter') || undefined;

      if (modelId) {
        this.openBuilder(modelId, baseProductSku, sessionId, resume, inputParameter);
      }

      return false;
    }
  }

  /**
   * Handle hash changes for deep linking
   * Example: #configure&model=model-short-name&sessionId=abc123
   */
  async handleHashChange() {
    const hash = window.location.hash;

    // Check if hash contains 'configure'
    if (hash.includes('configure')) {
      const paramsString = hash.substring(1).split('&').join('&');
      const params = new URLSearchParams(paramsString);

      const sessionId = params.get('sessionId');

      if (sessionId) {
        // Open builder without specific modelId (will be handled by session)
        await this.openBuilder(null, null);
        return
      }

      const modelShortName = params.get('model');
      if(modelShortName) {
        // Wait for config to be available before reading dcConfig
        try {
          await this.configPromise;
        } catch (error) {
          console.warn('[HiddenAnchor] Config load failed in hashChange, continuing anyway:', error);
        }

        const dcConfigs = window.compatioConfig?.compatibleProducts?.dcConfig;

        if(Array.isArray(dcConfigs)) {
          const config = dcConfigs.find(
            (item) => item.builderType === 'sb3' &&
                      modelShortName === item.details?.builderUrl
          );
          const modelId = config?.modelId;

          if(modelId) await this.openBuilder(modelId, null);
        }
      }
    }
  }

  /**
   * Open the builder with specified model
   * Waits for configuration to load before opening
   */
  async openBuilder(modelId, baseProductSku, sessionId, resume, inputParameter) {
    if (this.state.isOpen) return; // Already open

    this.state.isOpen = true;
    this.state.modelId = modelId;
    this.state.baseProductSku = baseProductSku;
    this.state.inputParameter = inputParameter;

    // Lock body scroll
    this.lockBodyScroll();

    // Show loading spinner
    this.createLoadingSpinner();

    // Wait for configuration to load before rendering builder
    // This ensures window.compatioConfig is populated when AppContext initializes
    console.log('[HiddenAnchor] Waiting for configuration to load...');
    try {
      await this.configPromise;
      console.log('[HiddenAnchor] Configuration loaded, rendering builder');
    } catch (error) {
      console.warn('[HiddenAnchor] Configuration load failed, continuing anyway:', error);
      // Continue even if config fails - app will use fallback config
    }

    // Load and render builder
    this.loadAndRenderBuilder(modelId, baseProductSku, sessionId, resume, inputParameter);
  }

  /**
   * Close the builder and cleanup
   */
  closeBuilder() {
    this.state.isOpen = false;

    // Unmount React root
    if (this.state.reactRoot) {
      try {
        this.state.reactRoot.unmount();
      } catch (e) {
        // Ignore unmount errors
      }
      this.state.reactRoot = null;
    }

    // Remove DOM elements
    if (this.state.builderRootElement) {
      this.state.builderRootElement.remove();
      this.state.builderRootElement = null;
    }

    this.state.builderContainer = null;

    // Unlock body scroll
    this.unlockBodyScroll();

    // Reset state
    this.state.modelId = null;
    this.state.baseProductSku = null;
  }

  /**
   * Lock body scroll when builder opens
   */
  lockBodyScroll() {
    const body = document.querySelector('body');
    if (body) {
      // Save original overflow value
      if (this.state.originalScroll === null) {
        this.state.originalScroll = body.style.overflow;
      }
      body.style.overflow = 'hidden';
    }
  }

  /**
   * Unlock body scroll when builder closes
   */
  unlockBodyScroll() {
    const body = document.querySelector('body');
    if (body && this.state.originalScroll !== null) {
      body.style.overflow = this.state.originalScroll;
    }
  }

  /**
   * Create and show loading spinner
   * Matches the current Preact implementation's HTML structure
   */
  createLoadingSpinner() {
    // Root container
    const root = document.createElement('div');
    root.className = 'sb3-contain sb3-hidden-root';

    // Popup overlay
    const popup = document.createElement('div');
    popup.className = 'sb3-fixed sb3-top-0 sb3-left-0 sb3-w-screen sb3-max-w-screen sb3-h-screen sb3-bg-[rgba(0,0,0,0.75)] sb3-overflow-auto sb3-z-[20000]';

    // White background
    const bg = document.createElement('div');
    bg.className = 'sb3-bg-white sb3-h-screen sb3-overflow-auto sb3-flex sb3-items-center sb3-justify-center';

    // Spinner
    const spinner = document.createElement('div');
    spinner.className = 'sb3-loading-spinner sb3-w-10 sb3-h-10 sb3-border-4 sb3-border-blue-500 sb3-border-t-transparent sb3-rounded-full sb3-animate-spin';
    spinner.setAttribute('role', 'status');
    spinner.setAttribute('aria-label', 'Loading');

    // Assemble DOM tree
    bg.appendChild(spinner);
    popup.appendChild(bg);
    root.appendChild(popup);

    // Add to page
    document.body.appendChild(root);

    // Store references
    this.state.builderRootElement = root;
    this.state.builderContainer = popup;
  }

  /**
   * Dynamically load React, ReactDOM, and BuilderLoader
   * Then render the builder into the container
   */
  async loadAndRenderBuilder(modelId, baseProductSku, sessionId, resume, inputParameter) {
    try {
      // Dynamic imports - only load when needed
      const [React, ReactDOM, { default: BuilderLoader }] = await Promise.all([
        import('react'),
        import('react-dom/client'),
        import('components/BuilderLoader')
      ]);

      // Clear spinner, keep container
      const container = this.state.builderContainer;
      if (!container) return; // Builder was closed before loading finished

      container.innerHTML = ''; // Clear spinner

      // Apply inputParameter for the new builder before rendering
      // This ensures the popup builder's API calls use the correct params
      if (inputParameter !== undefined && window.compatioConfig) {
        try {
          window.compatioConfig.inputParameter = typeof inputParameter === 'string'
            ? JSON.parse(inputParameter)
            : inputParameter;
        } catch {
          window.compatioConfig.inputParameter = inputParameter;
        }
      }

      // Create React root
      this.state.reactRoot = ReactDOM.createRoot(container);

      // Render BuilderLoader
      // For HIDDEN type: setVariants=false, blank=false
      this.state.reactRoot.render(
        React.createElement(BuilderLoader, {
          initialModelId: modelId,
          initialSessionId: sessionId,
          initialResume: resume,
          isOpen: true,
          setIsOpen: () => this.closeBuilder(),
          blank: false,
          setVariants: false,
          baseProductSku: baseProductSku,
          setBaseProductSku: null
        })
      );
    } catch (error) {
      console.error('[HiddenAnchor] Failed to load builder', error);
      // On error, close builder gracefully
      this.closeBuilder();
    }
  }

  /**
   * Cleanup - remove all event listeners
   * Call this when page unloads or anchor is destroyed
   */
  cleanup() {
    // Close builder if open
    if (this.state.isOpen) {
      this.closeBuilder();
    }

    // Remove event listeners
    document.removeEventListener('click', this.clickHandler, true);
    window.removeEventListener('hashchange', this.hashHandler);
  }
}
